/**
 * @ngdoc service
 * @name avatarService
 * @module flowingly.services
 *
 * @description A service repsonsible for defining content for avatar circles / flows icons
 *
 * ## Notes
 *
 * ###API
 * * getFlowInitials - flows are represented by intials, return for this flow
 * * getUserInitial - return first letter of this users name
 * * getColour - look up colour
 * * getModelerNodeAvatarUrl - get avatar url for a node in the flow model
 *
 * Converted to ts on 14/01/2020
 * See https://bitbucket.org/flowingly-team/flowingly-source-code/src/b8e3fc0a689eca68d3c5e1718e1c54635975cbe3/src/Flowingly.Shared.Angular/flowingly.services/avatar.service.js?at=master
 */

'use strict';
import angular from 'angular';
import AVATAR_COLOR_PALETTE from './data/avatarColorPalette';

angular.module('flowingly.services').factory('avatarService', avatarService);

avatarService.$inject = ['lodashService'];

function avatarService(lodashService: Lodash) {
  let _palette;
  let _actorsWithAvatars = null; // The actors and their avatar urls which have been retrieved from server side

  const FlowinglyOrange = '#F57C00';

  const service = {
    getFlowInitials: getFlowInitials,
    getUserInitial: getUserInitial,
    getColour: getColour,
    getModelerNodeAvatarUrl: getModelerNodeAvatarUrl,
    setActorsWithAvatars: setActorsWithAvatars,
    getAvatarList: getAvatarList,
    haveAvatarList: haveAvatarList,
    getBgColorForAvatarWithFullName: getBgColorForAvatarWithFullName
  };

  initialisePalette();

  return service;

  //////////// Public API Methods

  function haveAvatarList() {
    if (_actorsWithAvatars === null) return false;
    return true;
  }

  function getAvatarList() {
    return _actorsWithAvatars;
  }

  function setActorsWithAvatars(data) {
    _actorsWithAvatars = data;
    lodashService.forEach(_actorsWithAvatars, function (row) {
      if (row.id === undefined) {
        row.id = row.Id;
        row.isAvatar = row.IsAvatar;
        row.avatarUrl = row.AvatarUrl;
        row.avatarId = row.AvatarId;
        row.name = row.Name;
      }
      if (!row.isAvatar) {
        row.avatarUrl = null;
      }
    });
  }

  function getModelerNodeAvatarUrl(actorId, actorName) {
    //FLOW-3045 Removed avatar file functionality - for now at least
    // var avatarUrl = getAvatarUrl(actorId);
    //if (avatarUrl !== undefined && avatarUrl !== null) {
    //  return avatarUrl;
    // }

    const firstLetter = actorName && actorName.charAt(0).toLowerCase();

    return firstLetter && firstLetter.match(/[a-z]/)
      ? ASSETS_PATH + `/avatars/avatar_${firstLetter}_28.png`
      : '';
  }

  function getFlowInitials(flowName, flowCode = undefined) {
    //use code from data (returned from DB)
    if (flowCode) {
      return flowCode;
    }
    //else generate one after first removing all alphanumerics
    //we dont remove spaces as we use this to split names into words
    //dahses are converted to spaces
    //the filter then removes any no length words ("") from the word array;
    const sanitised = flowName
      .replace(/-/, ' ')
      .replace(/[^\w\s]/gi, '')
      .toUpperCase();
    const words = sanitised.split(' ').filter(Boolean);
    //if there is only one word
    if (words.length === 1 && words[0].length > 1) {
      return words[0].charAt(0) + words[0].charAt(1);
    } else if (words.length > 1) {
      return words[0].charAt(0) + words[1].charAt(0);
    }
    if (words.length > 0) {
      return words[0].charAt(0);
    }
  }

  function getUserInitial(firstName) {
    return firstName.charAt(0);
  }

  function getColour(name) {
    return lookupColour(name);
  }

  function getBgColorForAvatarWithFullName(fullName: string) {
    if (!fullName?.trim()) {
      return FlowinglyOrange;
    }

    let hash = 0;
    for (let i = 0; i < fullName.length; i++) {
      hash = fullName.charCodeAt(i) + ((hash << 5) - hash); //simple string-hashing technique
    }

    const index = Math.abs(hash) % AVATAR_COLOR_PALETTE.length;
    return AVATAR_COLOR_PALETTE[index];
  }

  //////////// Private Methods

  function actorHasAvatar(actorId) {
    const match = lodashService.find(_actorsWithAvatars, (a) => {
      return a.id === actorId;
    });
    return match;
  }

  function initialisePalette() {
    _palette = [
      '#FF8E8E', //1
      '#E994AB',
      '#67C7E2',
      '#A5D3CA',
      '#99C7FF', //5
      '#A8E4FF',
      '#75ECFD',
      '#92FEF9',
      '#7DFDD7',
      '#8BFEA8', //10
      '#FFACEC',
      '#EFA9FE',
      '#C4ABFE',
      '#EEEEA2' //14
    ];
  }

  function lookupColour(name) {
    const hex = hexEncode(name);
    const hexint = parseInt(hex, 16);
    const hue = getColorId(hexint);
    return getColourFromPalette(hue);
  }

  function getColorId(hexint) {
    const numColors = 14;
    let ranged = hexint;
    while (ranged > numColors) {
      ranged = ranged / numColors;
    }
    return parseInt(ranged);
  }

  function hexEncode(input) {
    let i = 0;
    let h = '';
    while (i < input.length) {
      h += input.charCodeAt(i++).toString(16);
    }
    return h;
  }

  function getColourFromPalette(index) {
    return _palette[index];
  }
}

export type AvatarServiceType = ReturnType<typeof avatarService>;
